import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/comics";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const location = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1400px] mx-auto">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <span className="text-xl md:text-2xl font-bold font-heading text-primary-600">
              韩漫屋
            </span>
            <span className="hidden sm:inline text-xs text-foreground-500 font-label">
              hanmanwu.com
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === "/"
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
              }`}
            >
              首页
            </Link>
            <Link
              to="/category/all"
              className={`px-4 py-2 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname.startsWith("/category")
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
              }`}
            >
              分类
            </Link>
            <Link
              to="/comic/3"
              className={`px-4 py-2 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname.startsWith("/comic")
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
              }`}
            >
              热门
            </Link>
            <Link
              to="/about"
              className={`px-4 py-2 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === "/about"
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              {searchOpen ? (
                <div className="flex items-center gap-2 bg-background-100 rounded-full px-3 py-1.5 border border-background-200/70">
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="搜索漫画..."
                    className="bg-transparent text-sm text-foreground-800 outline-none w-32 md:w-48 font-label placeholder:text-foreground-400"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setSearchOpen(false);
                        setSearchText("");
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchText("");
                    }}
                    className="text-foreground-400 hover:text-foreground-600 cursor-pointer"
                    aria-label="关闭搜索"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
                  aria-label="搜索"
                >
                  <i className="ri-search-line text-lg text-foreground-700"></i>
                </button>
              )}
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="菜单"
            >
              <i
                className={`text-lg text-foreground-700 ${
                  mobileOpen ? "ri-close-line" : "ri-menu-line"
                }`}
              ></i>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-background-200/70 py-3 px-2 bg-background-50">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className={`px-4 py-2.5 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === "/"
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                首页
              </Link>
              <Link
                to="/category/all"
                className={`px-4 py-2.5 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith("/category")
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                全部分类
              </Link>
              <Link
                to="/comic/3"
                className={`px-4 py-2.5 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith("/comic")
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                热门漫画
              </Link>
              <Link
                to="/about"
                className={`px-4 py-2.5 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === "/about"
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}